/**
 * broadcastStatusTransitions — EMAILS-IMPL-020, reconciled by EMAILS-IMPL-024
 * to BRD-SPEC-001 v2.6.0 / BRD-LIB-001 v1.5.0 (EMAILS-SPEC-015).
 *
 * Client-side mirror of email_marketing_broadcast_authoring_service.py's
 * own `_ALLOWED_STATUS_TRANSITIONS` table, used ONLY to decide which
 * transition buttons to render (an affordance decision, never an
 * authorization decision) -- the backend PATCH endpoint independently
 * re-validates every transition (including the Launch Reported Handoff
 * prerequisite, BRD-SPEC-001 §21.2) and remains the sole authority. If
 * this table ever drifts from the backend's own, the worst case is a
 * stale button that the backend then rejects with a normal 422 the
 * operator sees via MutationErrorAlert -- never an incorrectly ALLOWED
 * transition, since the backend is never bypassed.
 *
 * Deliberately excludes every invented status (Ready/Sent/Delivered/
 * Paused/Failed/Live) -- exactly the real, governed prospective
 * vocabulary and nothing else.
 */

// The prospective Day-1 vocabulary (BRD-SPEC-001 §21) -- what a NEW
// Broadcast, or an ordinary prospective transition on an existing one,
// may ever be set to. `Sending`/`Completed` are deliberately excluded --
// they are legacy read-compatible values only (§21.3), never a
// prospective authoring target; see LEGACY_BROADCAST_STATUS_VALUES below
// and ALLOWED_TRANSITIONS, which encodes their one governed historical
// exit path without ever offering them as a target from any prospective
// status.
export const BROADCAST_STATUS_VALUES = [
  "Draft",
  "Review",
  "Approved",
  "Scheduled",
  "Launch Reported",
  "Cancelled",
  "Archived",
];

// Legacy values a historical Broadcast record may already hold --
// readable/renderable, never offered as a prospective authoring target.
// See BRD-SPEC-001 §21.3.
export const LEGACY_BROADCAST_STATUS_VALUES = ["Sending", "Completed"];

const ALLOWED_TRANSITIONS = {
  Draft: ["Review", "Cancelled"],
  Review: ["Draft", "Approved", "Cancelled"],
  Approved: ["Scheduled", "Launch Reported", "Cancelled"],
  Scheduled: ["Launch Reported", "Cancelled"],
  "Launch Reported": ["Archived"],
  Cancelled: ["Archived"],
  Archived: [],
  // Legacy historical exit path only (BRD-SPEC-001 §21.3) -- neither
  // value ever appears as a target in any OTHER status's own entry
  // above, so no prospective transition can ever enter Sending or
  // Completed; these two entries exist solely so an already-legacy
  // record can still progress toward Archived.
  Sending: ["Completed"],
  Completed: ["Archived"],
};

export function legalNextStatuses(currentStatus) {
  return ALLOWED_TRANSITIONS[currentStatus] || [];
}

// EMAILS-IMPL-024, BRD-SPEC-001 §21.2: Launch Reported carries its own
// dedicated affordance (an explicit operator action, not a bare status
// button -- see BroadcastsPanel.jsx) and its own prerequisite the plain
// transition allow-list above cannot express (Snapshot existence is
// per-record data, not a function of current status alone). The backend
// independently re-validates this exact prerequisite and remains
// authoritative regardless of this affordance decision.
const LAUNCH_REPORTED_ELIGIBLE_STATUSES = new Set(["Approved", "Scheduled"]);

export function isLaunchReportedOfferable(broadcast) {
  return LAUNCH_REPORTED_ELIGIBLE_STATUSES.has(broadcast.status) && !!broadcast.rendered_handoff_snapshot;
}

// Provider-neutral governed values only (BRD-SPEC-001 §10.1) -- never
// SendGrid-specific terminology.
export const BROADCAST_ENVIRONMENT_VALUES = ["Qualification", "Production"];

// Mirrors email_marketing_handoff_snapshot_service.py's own
// `_ELIGIBLE_STATUSES` -- affordance only, same "backend remains
// authoritative" caveat as above.
const HANDOFF_ELIGIBLE_STATUSES = new Set(["Approved", "Scheduled"]);

export function isEligibleForHandoffGeneration(broadcast) {
  return !broadcast.rendered_handoff_snapshot && HANDOFF_ELIGIBLE_STATUSES.has(broadcast.status);
}

// EMAILS-IMPL-022: Broadcast Launch Readiness -- fully derived, never
// persisted (per EMAILS-022's own characterization: presence of the
// immutable Handoff Snapshot already proves the deep governed content
// resolved successfully at generation time; the ONLY thing this module
// adds on top is the current lifecycle status, since a Cancelled/Launch
// Reported/legacy Sending/legacy Completed/Archived Broadcast must never
// read as launch-ready merely because it still carries a historical
// Snapshot). This module deliberately does NOT re-implement
// email_marketing_handoff_snapshot_service.py's own deep eligibility gate
// (Creative Version Fingerprint resolution, Asset Version content,
// external Landing Page destination resolution) -- that logic stays
// backend-only and authoritative; a generation attempt's own 422 remains
// the truthful source for those failure modes. The three checks below
// are deliberately limited to fields ALREADY present on every fetched
// BroadcastResponse, and mirror (without duplicating the deep logic of)
// email_marketing_handoff_snapshot_service.py's own shallow,
// pre-deep-resolution eligibility checks (`_check_eligibility`'s
// environment/creative_version/frozen-Sender-fingerprint checks
// specifically -- not its status check, which is folded into the states
// below directly, nor its Snapshot-existence check, which for a
// Draft/Review Broadcast is always trivially true anyway since the
// backend transition table makes Approved/Scheduled unreachable from
// Draft/Review backward).
const LAUNCH_ELIGIBLE_STATUSES = new Set(["Approved", "Scheduled"]);
// EMAILS-IMPL-024, BRD-SPEC-001 §21.4: every status past the Launch Ready
// window -- the new operator-assertion state (`Launch Reported`), both
// legacy read-compatible values (`Sending`/`Completed`, BRD-SPEC-001
// §21.3), and `Archived` -- collapses to the SAME neutral, honest
// treatment below: "Handoff Generated" historical evidence when a
// Snapshot exists, never "Launch Ready," and never a claim of verified
// GHL/SendGrid delivery.
const TERMINAL_EXECUTION_STATUSES = new Set(["Launch Reported", "Sending", "Completed", "Archived"]);
const APPROVAL_PENDING_STATUSES = new Set(["Draft", "Review"]);

// Locally-verifiable prerequisites only -- never the deep Fingerprint/
// Landing-Page checks the backend alone re-validates live. Absence of any
// of these on an Approved/Scheduled, not-yet-generated Broadcast can only
// mean a historical/legacy record predating the governance that requires
// them (EMAILS-IMPL-018 Environment, EMAILS-IMPL-021 Sender Configuration,
// or an unresolved/never-pinned exact Creative Version) -- never a
// transient state a newly-authored Broadcast could ever be in, since the
// authoring service requires all three at creation and none is ever
// PATCHable afterward.
function missingLocalPrerequisites(broadcast) {
  const reasons = [];
  if (!broadcast.environment) {
    reasons.push("No Broadcast Environment is recorded on this record.");
  }
  if (!broadcast.creative_version) {
    reasons.push("No exact Creative Version is pinned on this record.");
  }
  const fingerprint = broadcast.broadcast_fingerprint || {};
  if (!fingerprint.sender || !fingerprint.sender_email || !fingerprint.reply_address) {
    reasons.push("No governed Sender Configuration values are frozen on this record.");
  }
  return reasons;
}

/**
 * evaluateBroadcastLaunchReadiness — the single derived-readiness
 * evaluation this panel uses everywhere (list indicator + detail
 * breakdown), so the two can never disagree. Returns:
 *   { state, label, detail?, reasons? }
 */
export function evaluateBroadcastLaunchReadiness(broadcast) {
  const hasSnapshot = !!broadcast.rendered_handoff_snapshot;
  const status = broadcast.status;

  if (hasSnapshot && LAUNCH_ELIGIBLE_STATUSES.has(status)) {
    return {
      state: "launch_ready",
      label: "Launch Ready",
      detail: "The immutable GHL Handoff has been generated. Open it to manually recreate and launch this Broadcast in GHL.",
    };
  }

  if (status === "Cancelled") {
    return {
      state: "cancelled",
      label: "Not Launch Ready",
      detail: "This Broadcast was Cancelled and is no longer a launch candidate.",
    };
  }

  if (TERMINAL_EXECUTION_STATUSES.has(status)) {
    if (hasSnapshot) {
      return {
        state: "handoff_generated_historical",
        label: "Handoff Generated",
        detail: "Historical Handoff evidence for this Broadcast's prior lifecycle stage -- not an instruction to launch, and not confirmation that GHL or SendGrid delivered anything.",
      };
    }
    return {
      state: "not_launch_ready",
      label: "Not Launch Ready",
      detail: "This Broadcast has moved past the launch-readiness stage of its lifecycle.",
    };
  }

  if (APPROVAL_PENDING_STATUSES.has(status)) {
    return {
      state: "approval_not_complete",
      label: "Not Launch Ready",
      detail: "Broadcast approval is still required before a Handoff can be generated.",
    };
  }

  // status is Approved or Scheduled, no Snapshot yet.
  const reasons = missingLocalPrerequisites(broadcast);
  if (reasons.length > 0) {
    return { state: "not_eligible", label: "Not Eligible for Handoff", reasons };
  }

  return {
    state: "handoff_not_generated",
    label: "Handoff not generated",
    detail: "Generating the Handoff validates and freezes the remaining governed content (Creative composition, Asset resolution, and Landing Page destination) before it is produced.",
  };
}
